import React, { useEffect, useRef, useState } from "react";
import { api, formatError, logDev } from "../../api";
import { Upload, FileText, Trash2, Star, Check, Download, Loader2 } from "lucide-react";

const ALLOWED_LABEL = ".pdf, .doc, .docx · max 5 MB";

function humanSize(bytes) {
  if (!Number.isFinite(bytes)) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function timeAgo(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "—";
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 30 * 86400) return `${Math.floor(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

/**
 * ResumeManager — full resume vault widget for the Student profile.
 *
 * Lists all uploads, shows which one is primary (auto-attached to new
 * applications), lets the student set primary, soft-delete, or upload
 * a new version. Used inside StudentDashboard.
 */
export default function ResumeManager() {
  const inputRef = useRef(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const load = async () => {
    setErr("");
    try {
      const r = await api.get("/me/resumes");
      setItems(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onPick = () => inputRef.current?.click();

  const upload = async (file) => {
    if (!file) return;
    setUploading(true); setErr(""); setMsg("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/me/resumes", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMsg("Resume uploaded — set as primary.");
      await load();
    } catch (e) {
      setErr(formatError(e));
      logDev.warn("[resume] upload failed", e);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const setPrimary = async (rid) => {
    setErr(""); setMsg("");
    try {
      await api.patch(`/me/resumes/${rid}/primary`);
      setMsg("Primary resume updated.");
      await load();
    } catch (e) {
      setErr(formatError(e));
    }
  };

  const remove = async (rid) => {
    if (!window.confirm("Delete this resume? Applications already submitted keep their attached copy.")) return;
    setErr(""); setMsg("");
    try {
      await api.delete(`/me/resumes/${rid}`);
      setMsg("Resume removed.");
      await load();
    } catch (e) {
      setErr(formatError(e));
    }
  };

  const download = async (rid, filename) => {
    try {
      const r = await api.get(`/me/resumes/${rid}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "resume.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setErr(formatError(e));
    }
  };

  return (
    <div data-testid="resume-manager" className="space-y-4">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow">Resume vault</div>
          <h3 className="font-display font-extrabold text-xl mt-1">Your resumes</h3>
          <p className="text-xs text-muted mt-1">
            The <strong>primary</strong> resume is auto-attached to every new application. {ALLOWED_LABEL}.
          </p>
        </div>
        <button
          type="button"
          onClick={onPick}
          disabled={uploading}
          data-testid="resume-upload-btn"
          className="btn-primary inline-flex items-center gap-2 disabled:opacity-60"
        >
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
          {uploading ? "Uploading…" : "Upload new"}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          className="hidden"
          onChange={(e) => upload(e.target.files?.[0])}
          data-testid="resume-file-input"
        />
      </div>

      {err && <div className="text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2" data-testid="resume-error">{err}</div>}
      {msg && <div className="text-sm text-emerald-700 bg-emerald-50 rounded-xl px-3 py-2" data-testid="resume-msg">{msg}</div>}

      {loading ? (
        <div className="text-sm text-muted">Loading resumes…</div>
      ) : items.length === 0 ? (
        <div className="border-2 border-dashed rounded-xl p-8 text-center" style={{ borderColor: "var(--rule)" }} data-testid="resume-empty">
          <FileText size={26} className="mx-auto text-muted mb-2" />
          <div className="font-semibold text-sm">No resume uploaded yet</div>
          <p className="text-xs text-muted mt-1">
            Upload a PDF or Word doc to apply to jobs with one click.
          </p>
          <button type="button" onClick={onPick} className="btn-secondary mt-4 inline-flex items-center gap-2">
            <Upload size={14} /> Upload your first resume
          </button>
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((r) => (
            <li
              key={r.id}
              data-testid={`resume-row-${r.id}`}
              className="flex items-center gap-3 p-3 rounded-xl border bg-white transition-shadow hover:shadow-sm min-w-0 overflow-hidden"
              style={{ borderColor: r.primary ? "rgb(0 108 255 / 0.4)" : "var(--rule)" }}
            >
              <div className="w-10 h-10 rounded-lg grid place-items-center shrink-0 bg-paper text-signal">
                <FileText size={18} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-semibold text-sm truncate block flex-1 min-w-0">{r.original_filename}</span>
                  {r.primary && (
                    <span className="shrink-0 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-signal text-white inline-flex items-center gap-1">
                      <Check size={10} /> Primary
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted truncate">{humanSize(r.size)} · {timeAgo(r.uploaded_at)}</div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {!r.primary && (
                  <button
                    type="button"
                    onClick={() => setPrimary(r.id)}
                    title="Set as primary"
                    aria-label="Set as primary"
                    data-testid={`resume-primary-${r.id}`}
                    className="w-9 h-9 grid place-items-center rounded-md text-muted hover:text-signal hover:bg-paper transition"
                  >
                    <Star size={15} />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => download(r.id, r.original_filename)}
                  title="Download"
                  aria-label="Download"
                  data-testid={`resume-download-${r.id}`}
                  className="w-9 h-9 grid place-items-center rounded-md text-muted hover:text-ink hover:bg-paper transition"
                >
                  <Download size={15} />
                </button>
                <button
                  type="button"
                  onClick={() => remove(r.id)}
                  title="Delete"
                  aria-label="Delete"
                  data-testid={`resume-delete-${r.id}`}
                  className="w-9 h-9 grid place-items-center rounded-md text-muted hover:text-red-600 hover:bg-red-50 transition"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
